import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import useDropdownOptions from '../../../hooks/useDropdownOptions';

const DynamicSelect = ({
  label,
  name,
  value,
  handleChange,
  error,
  required = false,
  tooltip,
  categoryId,
  className = '',
  helperText,
  placeholder = 'Sélectionnez...',
  disabled = false,
  refreshable = true, // Changé à true par défaut
  cacheMaxAge = 300000 // Réduit à 5 minutes par défaut
}) => {
  const { options, loading, error: apiError, refreshOptions } = useDropdownOptions(categoryId, {
    cacheMaxAge: cacheMaxAge,
    skipCache: false // Désactivé par défaut
  });

  const isDisabled = disabled || loading;

  // Vérifier explicitement si une erreur existe
  const hasError = error !== undefined && error !== null && error !== '';

  const handleRefresh = (e) => {
    e.preventDefault();
    refreshOptions(true); // Force l'invalidation du cache
  };

  // Refresh automatique à la première visite pour s'assurer que les données sont à jour
  useEffect(() => {
    // Vérifier si c'est la première visite pour ce composant
    const visitedKey = `select_visited_${categoryId}`;
    const hasVisited = sessionStorage.getItem(visitedKey);

    if (!hasVisited) {
      // Au premier rendu, force le rafraîchissement des données
      refreshOptions(true);
      // Marquer comme visité pour cette session
      sessionStorage.setItem(visitedKey, 'true');
    }
  }, [categoryId, refreshOptions]);

  // Log pour déboguer
  useEffect(() => {
    if (hasError) {
      console.log(`Select ${name} a une erreur:`, error);
    }
  }, [name, error, hasError]);

  return (
    <div className={`form-group ${className}`}>
      <label htmlFor={name} className="form-label text-start w-100">
        {label}
        {required && <span className="text-danger">*</span>}
        {tooltip && (
          <i
            className="bi bi-info-circle ms-2 text-muted cursor-pointer"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title={tooltip}
          ></i>
        )}
        {refreshable && (
          <button
            className="btn btn-link btn-sm p-0 ms-2"
            onClick={handleRefresh}
            disabled={loading}
            title="Rafraîchir les options"
          >
            <i className={`bi bi-arrow-clockwise ${loading ? 'spinner' : ''}`}></i>
          </button>
        )}
      </label>

      <div className="select-container">
        <select
          id={name}
          name={name}
          value={value || ''}
          onChange={handleChange}
          className={`form-select ${hasError ? 'is-invalid' : ''}`}
          required={required}
          disabled={isDisabled}
          aria-describedby={`${name}-helper`}
        >
          <option value="">{loading ? 'Chargement...' : placeholder}</option>
          {!loading && options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {loading && (
          <div className="spinner-border spinner-border-sm select-spinner" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        )}
      </div>

      {helperText && !hasError && (
        <small id={`${name}-helper`} className="form-text text-muted text-start">
          {helperText}
        </small>
      )}

      {/* Affichage explicite et forcé du message d'erreur */}
      {hasError && (
        <div className="invalid-feedback d-block text-start" style={{ display: 'block !important' }}>
          {error}
        </div>
      )}

      {apiError && !hasError && <div className="text-danger small mt-1">{apiError}</div>}

      <style jsx="true">{`
        .select-container {
          position: relative;
        }
        
        .select-spinner {
          position: absolute;
          right: 2.5rem;
          top: 50%;
          transform: translateY(-50%);
        }
        
        .spinner {
          display: inline-block;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        /* Forcer l'affichage des messages d'erreur */
        .invalid-feedback {
          display: block !important;
          color: #dc3545 !important;
          margin-top: 0.25rem !important;
          font-size: 0.875em !important;
        }
        
        .form-select.is-invalid {
          border-color: #dc3545 !important;
          padding-right: calc(1.5em + 0.75rem) !important;
          background-color: #fff8f8 !important;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23dc3545'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e") !important;
          background-repeat: no-repeat !important;
          background-position: right calc(0.375em + 0.1875rem) center !important;
          background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem) !important;
        }
      `}</style>
    </div>
  );
};

DynamicSelect.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  handleChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  required: PropTypes.bool,
  tooltip: PropTypes.string,
  categoryId: PropTypes.string.isRequired,
  className: PropTypes.string,
  helperText: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  refreshable: PropTypes.bool,
  cacheMaxAge: PropTypes.number
};

export default DynamicSelect;